import { CommonModule } from "@angular/common";
import { Component, DestroyRef, Input, OnChanges, SimpleChanges, computed, inject, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { UiLibraryModule } from "@template-ui/template-ui.module";
import { catchError, of } from "rxjs";
import {
  IpActivityEvent,
  IpActivityTimeline,
  IpActivityTimelineDisplayError,
  formatActivityReason,
  formatActivityWhen,
  mapActivityTimelineError,
} from "../../models/ip-activity-timeline.model";
import { IpActivityTimelineService } from "../../services/ip-activity-timeline.service";

/**
 * Read-only IP/candidate activity timeline panel rendered on the case-context page (story 937). Shows
 * the most-recent-first major workflow status events, and a graceful unavailable state when the
 * backend reports no activity backs the subject.
 */
@Component({
  selector: "phms-case-context-activity-timeline-panel",
  standalone: true,
  imports: [CommonModule, UiLibraryModule],
  templateUrl: "./case-context-activity-timeline-panel.component.html",
  styleUrl: "./case-context-activity-timeline-panel.component.scss",
})
export class CaseContextActivityTimelinePanelComponent implements OnChanges {
  private readonly destroyRef = inject(DestroyRef);
  private readonly timelineService = inject(IpActivityTimelineService);

  @Input({ required: true }) incarceratedPersonId!: number;

  readonly isLoading = signal(false);
  readonly errorState = signal<IpActivityTimelineDisplayError | null>(null);
  readonly timeline = signal<IpActivityTimeline | null>(null);

  readonly events = computed(() => this.timeline()?.events ?? []);
  readonly isUnavailable = computed(() => {
    const timeline = this.timeline();
    return timeline !== null && !timeline.dataAvailability.activity;
  });

  readonly formatWhen = formatActivityWhen;
  readonly formatReason = formatActivityReason;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["incarceratedPersonId"]) {
      this.load();
    }
  }

  load(): void {
    if (!this.incarceratedPersonId || this.incarceratedPersonId <= 0) {
      this.timeline.set(null);
      return;
    }

    this.isLoading.set(true);
    this.errorState.set(null);
    this.timeline.set(null);

    this.timelineService
      .getActivityTimeline(this.incarceratedPersonId)
      .pipe(
        catchError((error: unknown) => {
          this.errorState.set(mapActivityTimelineError(error));
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((timeline) => {
        this.timeline.set(timeline);
        this.isLoading.set(false);
      });
  }

  trackByEvent(index: number, event: IpActivityEvent): string {
    return `${event.occurredUtc}|${event.eventTypeCode}|${index}`;
  }
}
